import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { AppInstalableService } from '../../../core/services/app-instalable.service';
import { DialogoComponent } from '../dialogo/dialogo.component';
import { IconoComponent } from '../icono/icono.component';

/**
 * "Instalar la app" (D-147): abre la invitación del navegador o explica, según el navegador, cómo
 * agregarla a la pantalla de inicio. No aparece si ya está instalada.
 *   estilo "menu": tarjeta destacada arriba del menú lateral · "boton": botón con borde ·
 *   "principal": botón fucsia (la invitación que sube desde abajo)
 */
@Component({
  selector: 'app-instalar-app',
  imports: [DialogoComponent, IconoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (app.disponible()) {
      @switch (estilo()) {
        @case ('menu') {
          <button type="button" (click)="instalar()" class="mb-5 flex w-full items-center gap-3 rounded-2xl bg-fucsia-claro px-3 py-3 text-left hover:bg-fucsia/15">
            <img src="icono-192.png?v=4" alt="" width="40" height="40" class="size-10 shrink-0 rounded-xl" />
            <span class="min-w-0 flex-1">
              <span class="block font-semibold text-fucsia-oscuro">Instalar la app</span>
              <span class="block text-xs text-tinta-suave">Entra más rápido y recibe avisos</span>
            </span>
            <app-icono nombre="instalar" [tamano]="20" class="text-fucsia" />
          </button>
        }
        @case ('principal') {
          <button type="button" (click)="instalar()" class="inline-flex items-center gap-2 rounded-full bg-fucsia px-5 py-2.5 text-sm font-semibold text-white hover:bg-fucsia-oscuro">
            <app-icono nombre="instalar" [tamano]="18" /> Instalar
          </button>
        }
        @default {
          <button type="button" (click)="instalar()" class="inline-flex items-center gap-2 rounded-full border border-niebla px-4 py-2 text-sm font-semibold hover:border-tinta">
            <app-icono nombre="instalar" [tamano]="20" class="text-fucsia" /> Instalar la app
          </button>
        }
      }
    }

    <app-dialogo [(abierto)]="instrucciones" titulo="Agrégala a tu pantalla de inicio">
      @switch (app.plataforma) {
        @case ('ios-safari') {
          <ol class="list-decimal space-y-2 pl-5 text-[0.9375rem]">
            <li>Toca el botón <strong>Compartir</strong> de Safari (el cuadrado con la flecha hacia arriba, abajo en la pantalla).</li>
            <li>Baja y elige <strong>Agregar a inicio</strong>.</li>
            <li>Toca <strong>Agregar</strong>.</li>
          </ol>
        }
        @case ('ios-chrome') {
          <ol class="list-decimal space-y-2 pl-5 text-[0.9375rem]">
            <li>Toca el botón <strong>Compartir</strong> de Chrome (el cuadrado con la flecha hacia arriba, junto a la dirección).</li>
            <li>Elige <strong>Agregar a la pantalla de inicio</strong>. Si no lo ves, toca <strong>Más</strong> o <strong>Editar acciones</strong>.</li>
            <li>Toca <strong>Agregar</strong>.</li>
          </ol>
          <p class="mt-3 text-sm text-tinta-suave">Necesitas iOS 16.4 o más nuevo. Si no aparece la opción, ábrela en Safari.</p>
        }
        @case ('ios-otro') {
          <ol class="list-decimal space-y-2 pl-5 text-[0.9375rem]">
            <li>Abre el menú de <strong>Compartir</strong> del navegador.</li>
            <li>Elige <strong>Agregar a la pantalla de inicio</strong> y toca <strong>Agregar</strong>.</li>
          </ol>
          <p class="mt-3 text-sm text-tinta-suave">Si no aparece la opción, ábrela en Safari.</p>
        }
        @case ('android') {
          <ol class="list-decimal space-y-2 pl-5 text-[0.9375rem]">
            <li>Toca el menú del navegador (los tres puntos <strong>⋮</strong> arriba a la derecha).</li>
            <li>Elige <strong>Instalar app</strong> o <strong>Agregar a la pantalla principal</strong>.</li>
            <li>Confirma con <strong>Instalar</strong>.</li>
          </ol>
        }
        @default {
          <p class="text-[0.9375rem]">En Chrome o Edge, toca el ícono de instalar al final de la barra de direcciones, o el menú <strong>⋮</strong> → <strong>Instalar Pedidos SHEIN</strong>.</p>
        }
      }
      <p class="mt-3 text-sm text-tinta-suave">Queda como una app más en tu celular, y desde ahí puedes activar las notificaciones.</p>
      <div acciones>
        <button type="button" (click)="instrucciones.set(false)" class="rounded-full bg-tinta px-5 py-2.5 text-sm font-semibold text-white">Entendido</button>
      </div>
    </app-dialogo>
  `,
})
export class InstalarAppComponent {
  readonly app = inject(AppInstalableService);
  readonly estilo = input<'menu' | 'boton' | 'principal'>('boton');
  readonly instrucciones = signal(false);

  async instalar(): Promise<void> {
    if (!(await this.app.instalar())) this.instrucciones.set(true);
  }
}
